/**
 * StickyNotePanel Component
 *
 * Individual draggable panel for Goals or Focus of the Week.
 * Features:
 * - Draggable header (desktop only)
 * - Editable textarea with autosave
 * - Minimize button
 * - Resizable (desktop only)
 * - Responsive design
 * - Dynamics theme support
 * - Z-index management for focused state
 */

import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { motion, PanInfo, useReducedMotion, useDragControls } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { Minus, Target, Flame, Timer, GripHorizontal } from 'lucide-react';
import type { PanelType, Position, PanelConfig, PanelIconType, PanelSize } from '../../types/weeklyPanels';
import { dynamicsPanelColors, MIN_PANEL_SIZE, MAX_PANEL_SIZE, DEFAULT_PANEL_SIZE } from '../../types/weeklyPanels';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useUIStore } from '@shared/store/uiStore';

/**
 * Map icon type to Lucide component
 */
const IconMap: Record<PanelIconType, React.FC<{ size?: number; className?: string }>> = {
  target: Target,
  flame: Flame,
  timer: Timer,
};

interface StickyNotePanelProps {
  config: PanelConfig;
  content: string;
  position: Position;
  size?: PanelSize;
  isFocused?: boolean;
  onFocus?: () => void;
  onContentChange: (content: string) => void;
  onPositionChange: (position: Position) => void;
  onSizeChange?: (size: PanelSize) => void;
  onMinimize: () => void;
}

/**
 * Animation variants for panel
 */
const panelVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
  exit: { opacity: 0, scale: 0.9, y: 10, transition: { duration: 0.2 } },
};

/**
 * Reduced motion variants
 */
const reducedMotionVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

export const StickyNotePanel: React.FC<StickyNotePanelProps> = ({
  config,
  content,
  position,
  size,
  isFocused = false,
  onFocus,
  onContentChange,
  onPositionChange,
  onSizeChange,
  onMinimize,
}) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const dragControls = useDragControls();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [localContent, setLocalContent] = useState(content);
  const [isResizing, setIsResizing] = useState(false);

  // Use Dynamics theme color scheme when active
  const isDynamicsTheme = themeClass === 'theme-dynamics';
  const colors = useMemo(() => {
    return isDynamicsTheme ? dynamicsPanelColors[config.type] : config.colors;
  }, [isDynamicsTheme, config.type, config.colors]);

  // Sync local content with prop
  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  // Handle keyboard escape to minimize
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onMinimize();
      }
    },
    [onMinimize]
  );

  // Handle content change with local state
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value;
      setLocalContent(newContent);
      onContentChange(newContent);
    },
    [onContentChange]
  );

  // Track the position at drag start to calculate offset correctly
  const dragStartPosRef = useRef<Position>({ x: 0, y: 0 });

  // Handle drag start - capture current position
  const handleDragStart = useCallback(() => {
    dragStartPosRef.current = { x: position.x, y: position.y };
  }, [position.x, position.y]);

  // Handle drag end - use offset (delta) added to position at drag start
  // info.offset is the distance moved from drag start, add to the position we had at start
  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const newPosition: Position = {
        x: Math.max(0, Math.round(dragStartPosRef.current.x + info.offset.x)),
        y: Math.max(0, Math.round(dragStartPosRef.current.y + info.offset.y)),
      };
      onPositionChange(newPosition);
    },
    [onPositionChange]
  );

  // Start drag from header only
  const startDrag = useCallback(
    (event: React.PointerEvent) => {
      if (!isMobile) {
        dragControls.start(event);
      }
    },
    [dragControls, isMobile]
  );

  // Get current panel dimensions
  const currentWidth = size?.width ?? DEFAULT_PANEL_SIZE.width;
  const currentHeight = size?.height ?? DEFAULT_PANEL_SIZE.height;

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isMobile || !onSizeChange) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const startWidth = currentWidth;
    const startHeight = currentHeight;

    const handleMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
      const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const deltaX = currentX - startX;
      const deltaY = currentY - startY;

      const newWidth = Math.min(MAX_PANEL_SIZE.width, Math.max(MIN_PANEL_SIZE.width, startWidth + deltaX));
      const newHeight = Math.min(MAX_PANEL_SIZE.height, Math.max(MIN_PANEL_SIZE.height, startHeight + deltaY));

      onSizeChange({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
  }, [isMobile, currentWidth, currentHeight, onSizeChange]);

  const variants = prefersReducedMotion ? reducedMotionVariants : panelVariants;

  // Z-index management - focused panel appears on top
  const baseZIndex = 50;
  const focusedZIndex = 100;

  // Mobile: fixed position at top center
  // Desktop: draggable with saved position and custom size
  const panelStyle = isMobile
    ? {
        position: 'fixed' as const,
        top: '80px',
        left: '16px',
        right: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '400px',
        margin: '0 auto',
        zIndex: isFocused ? focusedZIndex : baseZIndex,
      }
    : {
        position: 'fixed' as const,
        left: position.x,
        top: position.y,
        width: currentWidth,
        height: currentHeight,
        minWidth: MIN_PANEL_SIZE.width,
        maxWidth: MAX_PANEL_SIZE.width,
        minHeight: MIN_PANEL_SIZE.height,
        maxHeight: MAX_PANEL_SIZE.height,
        zIndex: isFocused ? focusedZIndex : baseZIndex,
      };

  return (
    <motion.div
      className={`
        ${colors.bg} ${colors.border}
        border-2 rounded-xl shadow-lg
        overflow-hidden
        flex flex-col
        focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-500
      `}
      style={panelStyle}
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      drag={!isMobile}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={{
        top: 0,
        left: 0,
        right: typeof window !== 'undefined' ? window.innerWidth - currentWidth : 1000,
        bottom: typeof window !== 'undefined' ? window.innerHeight - currentHeight : 800,
      }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onPointerDown={onFocus}
      onKeyDown={handleKeyDown}
      role="complementary"
      aria-label={`${config.title} panel`}
      aria-expanded={true}
      tabIndex={0}
      data-testid={`sticky-panel-${config.type}`}
    >
      {/* Header - Drag Handle (desktop) */}
      <div
        onPointerDown={startDrag}
        className={`
          ${colors.header}
          px-3 py-2 flex items-center justify-between
          ${!isMobile ? 'cursor-move' : ''}
          select-none
          touch-none
        `}
      >
        <div className="flex items-center gap-2">
          {(() => {
            const IconComponent = IconMap[config.colors.icon];
            return <IconComponent size={18} className={colors.iconColor} aria-hidden="true" />;
          })()}
          <h3 className={`font-medium text-sm ${colors.headerText}`}>{config.title}</h3>
        </div>
        <button
          onClick={onMinimize}
          className={`
            p-1 rounded hover:bg-black/10 transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-brand-400
            ${isMobile ? 'min-w-[44px] min-h-[44px] flex items-center justify-center' : ''}
          `}
          aria-label={`Minimize ${config.title}`}
          data-testid={`minimize-${config.type}`}
        >
          <Minus size={isMobile ? 20 : 16} className="text-dark-muted" />
        </button>
      </div>

      {/* Body - Textarea (fills remaining space, no border, hidden scrollbar) */}
      <div className="px-3 pb-3 flex-1 flex flex-col min-h-0">
        <textarea
          ref={textareaRef}
          value={localContent}
          onChange={handleContentChange}
          placeholder={config.placeholder}
          className={`
            w-full h-full p-2 rounded flex-1
            bg-transparent border-none
            text-sm
            text-slate-950
            placeholder-slate-500
            resize-none
            focus:outline-none focus:ring-0
            overflow-y-auto
            scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-400/50
            [&::-webkit-scrollbar]:w-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-transparent
            [&::-webkit-scrollbar-thumb]:rounded-full
            hover:[&::-webkit-scrollbar-thumb]:bg-gray-400/50
            focus:[&::-webkit-scrollbar-thumb]:bg-gray-400/50
            ${isMobile ? 'text-base' : ''}
          `}
          onClick={(e) => e.stopPropagation()}
          aria-label={`${config.title} content`}
          data-testid={`textarea-${config.type}`}
        />
      </div>

      {/* Resize Handle (desktop only) */}
      {!isMobile && onSizeChange && (
        <div
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          onPointerDown={(e) => e.stopPropagation()}
          className={`
            absolute bottom-0 right-0 w-6 h-6
            flex items-center justify-center
            cursor-se-resize
            opacity-50 hover:opacity-100 transition-opacity
            ${isResizing ? 'opacity-100' : ''}
          `}
          aria-label="Resize panel"
          role="slider"
          aria-valuemin={MIN_PANEL_SIZE.width}
          aria-valuemax={MAX_PANEL_SIZE.width}
          aria-valuenow={currentWidth}
        >
          <GripHorizontal
            size={14}
            className={`${colors.iconColor} rotate-[-45deg]`}
          />
        </div>
      )}
    </motion.div>
  );
};
